/** @format */
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FiGlobe } from "react-icons/fi";
import PatientSchedulingEarlierAppoinmentNotify from "src/components/pages/patients/scheduling/dep/PatientSchedulingEarlierAppoinmentNotify";
import Bootstrap5Row from "src/components/shared/elements/Bootstrap5Row";
import { BROWSER_TIME_ZONE, BROWSER_TIME_ZONE_SHORT, DATE_TIME_FORMATS } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { daysjsFromUnix, logDev, zeroTime } from "src/utils";
import { LOBBIE_COLORS } from "src/utils/colors";
import { boldTextCSS, secondaryDarkerTextCSS } from "src/utils/klasses";
import PatientSchedulingCalendar from "./PatientSchedulingCalendar";
import PatientSchedulingTimeBlocks from "./PatientSchedulingTimeBlocks";

const PatientSchedulingPickAppointmentDateAndTime = () => {
    const {
        selfScheduleSettings,
        selfSchedule,
        setSelfSchedule,
        availability,
        setAvailability,
        getMonth,
    } = usePatientSelfSchedulingContext();

    const timeBlocksContainerRef = useRef<HTMLDivElement | null>(null);

    const timeZone = useMemo(() => BROWSER_TIME_ZONE, []);

    const selectedDate = useMemo(
        () => (selfSchedule?.date ? daysjsFromUnix(selfSchedule.date, timeZone) : undefined),
        [selfSchedule?.date, timeZone],
    );

    const selectedTime = useMemo(
        () =>
            selfSchedule?.time && selectedDate ? dayjs(selfSchedule.time).tz(timeZone) : undefined,
        [selfSchedule?.time, selectedDate, timeZone],
    );

    useEffect(() => {
        if (selfSchedule?.date) {
            timeBlocksContainerRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
        }
    }, [selfSchedule?.date]);

    useEffect(() => {
        if (selfSchedule?.time) {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [selfSchedule?.time]);

    const setSelectedMonth = useCallback(
        (month: Dayjs) => {
            setAvailability((prev) => ({
                ...prev,
                selectedMonth: month.month(),
                selectedYear: month.year(),
            }));
            const startDate = zeroTime(dayjs())
                .set("month", month.month())
                .set("year", month.year())
                .set("date", 1);
            const endDate = startDate.set("date", startDate.daysInMonth()).add(1, "day");
            getMonth(startDate.valueOf(), endDate.valueOf());
        },
        [getMonth, setAvailability],
    );

    const setIsNotifyEarlierAppointment = useCallback(
        (notify: boolean) => {
            logDev("PatientSchedulingCalendarV2.setIsNotifyEarlierAppointment ->", notify);
            if (selfSchedule) {
                setSelfSchedule({ ...selfSchedule, isNotifyEarlierAppointment: notify });
            }
        },
        [setSelfSchedule, selfSchedule],
    );

    const handleSelectDay = useCallback(
        (day: Dayjs) => {
            if (selfSchedule) {
                setSelfSchedule({ ...selfSchedule, date: day.valueOf(), time: null });
            }
        },
        [selfSchedule, setSelfSchedule],
    );

    if (!selfSchedule?.appointmentType) {
        return null;
    }

    return (
        <div className="cal-container">
            <Bootstrap5Row className="mt-4">
                <h3 className="extra-bold my-0 mr-3">Select your date & time:</h3>
            </Bootstrap5Row>
            <PatientSchedulingCalendar
                // TODO: SCHEDULER_2 update for different block sizes, monthSchedules[0].blockSize ||
                handleSelectDay={handleSelectDay}
                isLoadingScheduler={false}
                availabilities={availability.schedule}
                selectedDate={selectedDate || null}
                selectedMonth={availability.selectedMonth}
                setSelectedMonth={setSelectedMonth}
            />
            <div className="d-inline-block mt-2 border border-secondary rounded p-2">
                <FiGlobe color={LOBBIE_COLORS.primary} />
                &nbsp;Timezone:&nbsp;{BROWSER_TIME_ZONE}&nbsp;(
                {BROWSER_TIME_ZONE_SHORT})
            </div>
            {selfSchedule?.date && (
                <div ref={timeBlocksContainerRef}>
                    <PatientSchedulingTimeBlocks />
                    {selectedTime && (
                        <div className="d-flex flex-column align-items-center justify-content-center">
                            <p className={secondaryDarkerTextCSS}>
                                Selected{" "}
                                {selfScheduleSettings?.appointmentPatientFieldName.toLowerCase()}:
                            </p>
                            <p className={boldTextCSS}>
                                {`${(selectedDate as Dayjs)
                                    .tz(BROWSER_TIME_ZONE)
                                    .format(
                                        DATE_TIME_FORMATS.Appointment.DateFormat2NoYear,
                                    )}, ${selectedTime
                                    .tz(BROWSER_TIME_ZONE)
                                    .format(DATE_TIME_FORMATS.Appointment.PatientHomeTime)}`}
                            </p>
                        </div>
                    )}
                    <PatientSchedulingEarlierAppoinmentNotify
                        setIsNotifyEarlierAppointment={setIsNotifyEarlierAppointment}
                        appointmentLowerString={selfScheduleSettings?.appointmentPatientFieldName.toLowerCase()}
                    />
                </div>
            )}
        </div>
    );
};

export default PatientSchedulingPickAppointmentDateAndTime;
